import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import {
  MarketingStackWorkflowStrip,
  marketingStackCardClassName,
  marketingStackContentInsetClassName,
  marketingStackPageClassName,
} from "@/react/components/marketingStack";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { CheckSquare2, RotateCcw, Square } from "lucide-react";
import { showToast } from "@/other/utils/utils.jsx";
import { Button } from "@/modern/components/ui/button";
import { cn } from "@/modern/utils";
import { MarketingTag, MarketingTagType } from "@/bryntum-lib/models/MarketingTag";
import type { TemporalPhaseTag } from "@/react/components/marketing/temporal";
import {
  flattenFeedAssets,
  useSurfaceAssetMutations,
  useSurfaceFeedInfiniteQuery,
  useSurfaceFilterTagsQuery,
  useSurfaceProgressQuery,
  useSurfaceSolutionTagsQuery,
} from "../api/promoteSurface.api";
import type {
  PromoteSurfaceAsset,
  SurfaceFeedFilters,
  SurfaceTab,
  SurfaceWriteAction,
} from "../api/types";
import { EMPTY_SURFACE_FEED_FILTERS, hasActiveSurfaceFilters } from "../api/types";
import { assetTagIds, mergeTagIds } from "../utils/mergeTagIds";
import { getSharedActions } from "../utils/actionMatrix";
import PromoteSurfaceTabs from "./PromoteSurfaceTabs";
import PromoteSurfaceTabHint from "./PromoteSurfaceTabHint";
import PromoteSurfaceEmptyState from "./PromoteSurfaceEmptyState";
import PromoteSurfaceGrid from "./PromoteSurfaceGrid";
import PromoteSurfaceBulkAcceptSheet from "./PromoteSurfaceBulkAcceptSheet";
import PromoteSurfaceReviewOverlay from "./PromoteSurfaceReviewOverlay";
import PromoteSurfaceFilterBar from "./PromoteSurfaceFilterBar";
import PromoteSurfaceReanalyzeDialog from "./PromoteSurfaceReanalyzeDialog";
import type { ContentRoleTag } from "./PromoteSurfaceAcceptFields";

type Props = {
  boardUserAvailable: boolean;
};

type SelectionBulkAction = Exclude<SurfaceWriteAction, "reprocess">;

function PromoteSurfaceGateMessage({ message }: { message: string }) {
  return (
    <div className={`${marketingStackPageClassName} w-full overflow-hidden bg-background`}>
      <MarketingStackWorkflowStrip activeView={VIEW_TYPES.promote} />
      <div className={marketingStackContentInsetClassName}>
        <div className={marketingStackCardClassName()}>
          <div className="flex flex-1 items-center justify-center p-10 text-center text-muted-foreground">
            {message}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PromoteSurfaceView({ boardUserAvailable }: Props) {
  const { user, selectedBoard, project } = useSwivuBryntumContext();
  const boardSlug = (selectedBoard?.record as { slug?: string } | undefined)?.slug;
  const canView = Boolean(user?.can?.(GRANTS.grant_six));

  const { version: marketingTagStoreVersion } = useSwivuStore<MarketingTag>({
    storeId: STORE_KEYS.marketingTagStore,
  });

  const temporalTags = useMemo<TemporalPhaseTag[]>(() => {
    const records =
      (project?.marketingTagStore?.query(
        (t: MarketingTag) => t.type === MarketingTagType.TEMPORAL,
        true
      ) as MarketingTag[] | undefined) ?? [];
    return records.map((t) => ({
      id: t.id,
      label: t.label,
      description: t.description ?? undefined,
    }));
  }, [project, marketingTagStoreVersion]);

  const contentRoleTags = useMemo<ContentRoleTag[]>(() => {
    const records =
      (project?.marketingTagStore?.query(
        (t: MarketingTag) => t.type === MarketingTagType.CONTENT || Boolean(t.isFolderEligible),
        true
      ) as MarketingTag[] | undefined) ?? [];
    return records.map((t) => ({
      id: t.id,
      label: t.label,
      description: t.description ?? undefined,
    }));
  }, [project, marketingTagStoreVersion]);

  const temporalTagIds = useMemo(() => temporalTags.map((t) => t.id), [temporalTags]);
  const contentRoleTagIds = useMemo(
    () => contentRoleTags.map((t) => t.id),
    [contentRoleTags]
  );

  const [activeTab, setActiveTab] = useState<SurfaceTab>("review");
  const [feedFilters, setFeedFilters] = useState<SurfaceFeedFilters>(EMPTY_SURFACE_FEED_FILTERS);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [focusedAssetId, setFocusedAssetId] = useState<number | null>(null);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [bulkAcceptOpen, setBulkAcceptOpen] = useState(false);
  const [rangeAnchorId, setRangeAnchorId] = useState<number | null>(null);
  const [reanalyzeOpen, setReanalyzeOpen] = useState(false);
  /** After a full select-all, hide Select while the user peels items off. */
  const [selectAllSession, setSelectAllSession] = useState(false);

  const progressQuery = useSurfaceProgressQuery(boardSlug, canView);
  const feedQuery = useSurfaceFeedInfiniteQuery(boardSlug, activeTab, canView, feedFilters);
  const tagsQuery = useSurfaceSolutionTagsQuery(boardSlug, canView);
  const filterTagsQuery = useSurfaceFilterTagsQuery(boardSlug, canView);
  const { runSingle, runBulk, runFanOut, reanalyzePending, getAxiosMessage } =
    useSurfaceAssetMutations(boardSlug, selectedBoard?.id);

  const assets = useMemo(() => flattenFeedAssets(feedQuery.data), [feedQuery.data]);
  const assetById = useMemo(() => new Map(assets.map((a) => [a.id, a])), [assets]);

  const selectedAssets = useMemo(
    () => selectedIds.map((id) => assetById.get(id)).filter(Boolean) as PromoteSurfaceAsset[],
    [selectedIds, assetById]
  );

  const sharedActions = useMemo(
    () => getSharedActions(selectedAssets.map((a) => a.verification_status)),
    [selectedAssets]
  );

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setRangeAnchorId(null);
    setSelectAllSession(false);
  }, []);

  const onTabChange = useCallback(
    (tab: SurfaceTab) => {
      setActiveTab(tab);
      clearSelection();
      setOverlayOpen(false);
      setFocusedAssetId(null);
    },
    [clearSelection]
  );

  const onFiltersChange = useCallback(
    (next: SurfaceFeedFilters) => {
      setFeedFilters(next);
      clearSelection();
      setOverlayOpen(false);
      setFocusedAssetId(null);
    },
    [clearSelection]
  );

  const selectAsset = useCallback(
    (assetId: number, opts: { range?: boolean } = {}) => {
      const index = assets.findIndex((a) => a.id === assetId);
      if (index < 0) return;

      if (opts.range && rangeAnchorId != null) {
        const anchorIndex = assets.findIndex((a) => a.id === rangeAnchorId);
        if (anchorIndex >= 0) {
          const [from, to] = anchorIndex < index ? [anchorIndex, index] : [index, anchorIndex];
          setSelectedIds(assets.slice(from, to + 1).map((a) => a.id));
          setFocusedAssetId(assetId);
          return;
        }
      }

      setSelectedIds((prev) =>
        prev.includes(assetId) ? prev.filter((id) => id !== assetId) : [...prev, assetId]
      );
      setRangeAnchorId(assetId);
      setFocusedAssetId(assetId);
    },
    [assets, rangeAnchorId]
  );

  const selectAllLoaded = useCallback(() => {
    setSelectedIds(assets.map((a) => a.id));
    setFocusedAssetId(assets[0]?.id ?? null);
    setSelectAllSession(true);
  }, [assets]);

  useEffect(() => {
    if (selectedIds.length === 0) {
      setSelectAllSession(false);
      return;
    }
    if (assets.length > 0 && selectedIds.length === assets.length) {
      setSelectAllSession(true);
    }
  }, [selectedIds.length, assets.length]);

  const prevLoadedCountRef = useRef(0);
  useEffect(() => {
    const prev = prevLoadedCountRef.current;
    prevLoadedCountRef.current = assets.length;
    // New rows loaded after a select-all — allow Select again for the expanded set.
    if (selectAllSession && assets.length > prev && selectedIds.length < assets.length) {
      setSelectAllSession(false);
    }
  }, [assets.length, selectAllSession, selectedIds.length]);

  const openOverlay = useCallback((assetId: number) => {
    setFocusedAssetId(assetId);
    setOverlayOpen(true);
  }, []);

  const closeOverlay = useCallback(() => {
    setOverlayOpen(false);
  }, []);

  const toastError = useCallback(
    (error: unknown) => {
      showToast({ msg: getAxiosMessage(error), type: "error", duration: "medium" });
    },
    [getAxiosMessage]
  );

  const deselectAssetIds = useCallback((ids: number[]) => {
    if (!ids.length) return;
    const drop = new Set(ids);
    setSelectedIds((prev) => prev.filter((id) => !drop.has(id)));
  }, []);

  const runAction = useCallback(
    async (action: SurfaceWriteAction, assetIds: number[], tagIds?: number[]) => {
      if (!boardUserAvailable && action !== "reprocess") {
        showToast({
          msg: "Board user required for this action. If it fails, ask an admin to add you to the board.",
          type: "warn",
          duration: "medium",
        });
        return;
      }

      try {
        if (assetIds.length === 1 && action !== "accept") {
          await runSingle.mutateAsync({ action, assetId: assetIds[0] });
        } else if (assetIds.length === 1 && action === "accept") {
          await runSingle.mutateAsync({
            action,
            assetId: assetIds[0],
            body: { tag_ids: tagIds ?? [] },
          });
        } else {
          const result = await runBulk.mutateAsync({
            action: action as SelectionBulkAction,
            asset_ids: assetIds,
            tag_ids: tagIds,
          });
          const failed = (result.results ?? []).filter((r) => !r.ok);
          const succeeded = (result.results ?? []).filter((r) => r.ok).map((r) => r.asset_id);
          if (result.mode === "async") {
            showToast({
              msg: `Started bulk ${action} for ${result.count ?? assetIds.length} images — keep an eye on progress.`,
              type: "warn",
              duration: "medium",
            });
            return;
          }
          if (!(result.results ?? []).length && assetIds.length > 0) {
            showToast({
              msg: "Bulk update returned no per-asset results — selection kept.",
              type: "warn",
              duration: "medium",
            });
            return;
          }
          if (failed.length) {
            showToast({
              msg: `${succeeded.length} succeeded, ${failed.length} failed`,
              type: "warn",
              duration: "medium",
            });
            setSelectedIds(failed.map((f) => f.asset_id));
          } else {
            clearSelection();
            showToast({
              msg: `Updated ${assetIds.length} assets`,
              type: "success",
              duration: "medium",
            });
          }
          return;
        }

        if (action === "reprocess") {
          showToast({ msg: "Re-queued for AI", type: "success", duration: "medium" });
        }
        clearSelection();
      } catch (error) {
        toastError(error);
      }
    },
    [boardUserAvailable, clearSelection, runBulk, runSingle, toastError]
  );

  const confirmReanalyze = useCallback(async () => {
    try {
      const result = await reanalyzePending.mutateAsync({ async: true });
      setReanalyzeOpen(false);
      showToast({
        msg:
          result.mode === "async"
            ? "Reanalyze started — pending drafts will refill as AI finishes."
            : "Reanalyze completed.",
        type: "success",
        duration: "medium",
      });
    } catch (error) {
      toastError(error);
    }
  }, [reanalyzePending, toastError]);

  const onBulkAccept = useCallback(
    async (payload: {
      assets: PromoteSurfaceAsset[];
      solutionTagIds: number[];
      temporalTagId: number | null;
      contentRoleIds: number[];
    }) => {
      if (!boardUserAvailable) {
        showToast({
          msg: "Board user required for this action. If it fails, ask an admin to add you to the board.",
          type: "warn",
          duration: "medium",
        });
        return;
      }

      const tags = tagsQuery.data ?? [];
      const mergeOpts = {
        temporalTagId: payload.temporalTagId,
        temporalTagIds,
        contentRoleIds: payload.contentRoleIds,
        contentRoleTagIds,
      };
      const targets = payload.assets.length ? payload.assets : selectedAssets;

      if (!targets.length) {
        showToast({
          msg: "No selected assets to accept.",
          type: "warn",
          duration: "medium",
        });
        return;
      }

      try {
        if (targets.length === 1) {
          const asset = targets[0];
          const tag_ids = mergeTagIds(
            assetTagIds(asset),
            payload.solutionTagIds,
            tags,
            mergeOpts
          );
          await runAction("accept", [asset.id], tag_ids);
        } else {
          const result = await runFanOut.mutateAsync(
            targets.map((asset) => ({
              action: "accept" as const,
              assetId: asset.id,
              body: {
                tag_ids: mergeTagIds(
                  assetTagIds(asset),
                  payload.solutionTagIds,
                  tags,
                  mergeOpts
                ),
              },
            }))
          );
          const failedIds = (result.results ?? [])
            .filter((r) => !r.ok)
            .map((r) => r.asset_id);
          if (failedIds.length) {
            showToast({
              msg: `${targets.length - failedIds.length} succeeded, ${failedIds.length} failed`,
              type: "warn",
              duration: "medium",
            });
            setSelectedIds(failedIds);
          } else {
            clearSelection();
            showToast({
              msg: `Accepted ${targets.length} assets`,
              type: "success",
              duration: "medium",
            });
          }
        }
        setBulkAcceptOpen(false);
      } catch (error) {
        toastError(error);
        throw error;
      }
    },
    [
      clearSelection,
      boardUserAvailable,
      runAction,
      runFanOut,
      selectedAssets,
      tagsQuery.data,
      temporalTagIds,
      contentRoleTagIds,
      toastError,
    ]
  );

  const overlayIndex = useMemo(() => {
    if (!overlayOpen || focusedAssetId == null) return 0;
    const idx = assets.findIndex((a) => a.id === focusedAssetId);
    return idx;
  }, [assets, focusedAssetId, overlayOpen]);

  useEffect(() => {
    if (!overlayOpen || focusedAssetId == null) return;
    if (overlayIndex < 0) {
      setOverlayOpen(false);
      setFocusedAssetId(null);
    }
  }, [overlayOpen, focusedAssetId, overlayIndex]);

  if (!canView) {
    return (
      <PromoteSurfaceGateMessage message="You need marketing (G6) access to open Promote." />
    );
  }

  if (!boardSlug) {
    return <PromoteSurfaceGateMessage message="Select a board to load the Promote queue." />;
  }

  const advanceOrCloseOverlay = (currentAssetId: number) => {
    const idx = assets.findIndex((a) => a.id === currentAssetId);
    const next =
      (idx >= 0 ? assets[idx + 1] : undefined) ?? (idx > 0 ? assets[idx - 1] : undefined);
    if (next && next.id !== currentAssetId) {
      setFocusedAssetId(next.id);
    } else {
      closeOverlay();
      setFocusedAssetId(null);
      showToast({
        msg: "All done in this set",
        type: "success",
        duration: "short",
      });
    }
  };

  const isEmpty = !feedQuery.isLoading && assets.length === 0;
  const isTabSwitching = Boolean(feedQuery.isFetching && feedQuery.isPlaceholderData);
  const showHint = !isEmpty || isTabSwitching;
  const mutationBusy =
    runSingle.isPending || runBulk.isPending || runFanOut.isPending || reanalyzePending.isPending;
  const loadedCount = isEmpty || isTabSwitching ? 0 : assets.length;
  const selectedCount = selectedIds.length;
  const showSelectAll =
    !overlayOpen &&
    loadedCount > 1 &&
    selectedCount < loadedCount &&
    !selectAllSession;
  const showClear = !overlayOpen && selectedCount > 0;
  const headerChip =
    "h-8 shrink-0 gap-1 rounded-full px-2.5 text-xs font-semibold shadow-none [&_svg]:size-3.5 sm:h-9";

  // Prefer a stable non-negative index for the overlay; close when focus is gone (effect above).
  const safeOverlayIndex = overlayIndex >= 0 ? overlayIndex : 0;

  return (
    <div
      className={`promote-surface-page ${marketingStackPageClassName} w-full overflow-hidden bg-background`}
    >
      <MarketingStackWorkflowStrip activeView={VIEW_TYPES.promote} />
      <div className={marketingStackContentInsetClassName}>
        <div className={marketingStackCardClassName()}>
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="relative z-20 flex shrink-0 items-center gap-2 px-2.5 pb-1.5 pt-2.5 sm:gap-3 sm:px-3 sm:pt-3">
              <div className="flex min-w-0 flex-1 items-center justify-start">
                <PromoteSurfaceFilterBar
                  filters={feedFilters}
                  filterTags={filterTagsQuery.data ?? []}
                  tagsLoading={filterTagsQuery.isLoading}
                  onChange={onFiltersChange}
                />
              </div>

              <div className="flex shrink-0 justify-center">
                <PromoteSurfaceTabs
                  activeTab={activeTab}
                  onChange={onTabChange}
                  progress={progressQuery.data}
                  selection={
                    !overlayOpen
                      ? {
                          count: selectedIds.length,
                          actions: sharedActions,
                          busy: mutationBusy,
                          onAccept: () => setBulkAcceptOpen(true),
                          onReject: () => runAction("reject", selectedIds),
                          onSkip: () => runAction("skip", selectedIds),
                        }
                      : null
                  }
                />
              </div>

              <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:gap-2">
                {showClear && (
                  <span
                    className="rounded-full bg-primary/10 px-2.5 py-1.5 text-xs tabular-nums text-primary"
                    aria-live="polite"
                  >
                    <span className="font-semibold">{selectedCount}</span>
                    <span className="ml-1 font-medium">selected</span>
                  </span>
                )}
                {showSelectAll && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className={cn(
                      headerChip,
                      "bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary"
                    )}
                    onClick={selectAllLoaded}
                    title={`Select the ${loadedCount} images loaded in this view`}
                  >
                    <CheckSquare2 aria-hidden />
                    Select {loadedCount}
                  </Button>
                )}
                {showClear && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className={cn(
                      headerChip,
                      "bg-muted/80 text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                    disabled={mutationBusy}
                    onClick={clearSelection}
                    title="Unselect all selected images"
                  >
                    <Square aria-hidden />
                    Unselect all
                  </Button>
                )}
                {activeTab === "review" && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-8 gap-1 rounded-full px-2.5 text-xs text-muted-foreground sm:h-9"
                    disabled={reanalyzePending.isPending || overlayOpen}
                    onClick={() => setReanalyzeOpen(true)}
                    title="Regenerate all pending Review drafts"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                    <span className="hidden sm:inline">Reanalyze</span>
                  </Button>
                )}
              </div>
            </div>

            <div className="min-h-[1.1rem] shrink-0">
              {showHint && <PromoteSurfaceTabHint tab={activeTab} />}
            </div>

            <div className="relative min-h-0 flex-1 overflow-hidden">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={`${activeTab}:${JSON.stringify(feedFilters)}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.16, ease: "easeOut" }}
                  className="absolute inset-0"
                >
                  {feedQuery.isError ? (
                    <div className="flex h-full flex-col items-center justify-center gap-3 p-8">
                      <p className="text-sm text-destructive">Failed to load feed.</p>
                      <button
                        type="button"
                        className="text-sm underline"
                        onClick={() => feedQuery.refetch()}
                      >
                        Retry
                      </button>
                    </div>
                  ) : isEmpty && !isTabSwitching ? (
                    <PromoteSurfaceEmptyState
                      tab={activeTab}
                      progress={progressQuery.data}
                      filtersActive={hasActiveSurfaceFilters(feedFilters)}
                    />
                  ) : (
                    <PromoteSurfaceGrid
                      assets={isTabSwitching ? [] : assets}
                      selectedIds={selectedIds}
                      focusedAssetId={focusedAssetId}
                      isLoading={feedQuery.isLoading || isTabSwitching}
                      isFetchingNextPage={feedQuery.isFetchingNextPage}
                      hasNextPage={Boolean(feedQuery.hasNextPage)}
                      fetchNextPage={() => feedQuery.fetchNextPage()}
                      onSelect={selectAsset}
                      onOpen={openOverlay}
                      tab={activeTab}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      <PromoteSurfaceBulkAcceptSheet
        open={bulkAcceptOpen}
        onOpenChange={setBulkAcceptOpen}
        tags={tagsQuery.data ?? []}
        temporalTags={temporalTags}
        contentRoleTags={contentRoleTags}
        selectedAssets={selectedAssets}
        busy={runFanOut.isPending || runSingle.isPending || runBulk.isPending}
        boardUserAvailable={boardUserAvailable}
        onConfirm={onBulkAccept}
      />

      <PromoteSurfaceReanalyzeDialog
        open={reanalyzeOpen}
        onOpenChange={setReanalyzeOpen}
        busy={reanalyzePending.isPending}
        onConfirm={confirmReanalyze}
      />

      <PromoteSurfaceReviewOverlay
        open={overlayOpen && overlayIndex >= 0}
        assets={assets}
        index={safeOverlayIndex}
        tags={tagsQuery.data ?? []}
        temporalTags={temporalTags}
        contentRoleTags={contentRoleTags}
        boardUserAvailable={boardUserAvailable}
        busy={mutationBusy}
        onOpenChange={(open) => {
          if (!open) closeOverlay();
          else setOverlayOpen(true);
        }}
        onIndexChange={(idx) => {
          const asset = assets[idx];
          if (asset) setFocusedAssetId(asset.id);
        }}
        onAccept={async (asset, solutionTagIds, extras) => {
          const { temporalTagId, contentRoleIds, ...rest } = extras ?? {};
          const tag_ids = mergeTagIds(
            assetTagIds(asset),
            solutionTagIds,
            tagsQuery.data ?? [],
            {
              temporalTagId,
              temporalTagIds,
              contentRoleIds: contentRoleIds ?? [],
              contentRoleTagIds,
            }
          );
          try {
            await runSingle.mutateAsync({
              action: "accept",
              assetId: asset.id,
              body: { tag_ids, ...rest },
            });
            deselectAssetIds([asset.id]);
            advanceOrCloseOverlay(asset.id);
          } catch (error) {
            toastError(error);
            throw error;
          }
        }}
        onAction={async (action, asset) => {
          try {
            await runSingle.mutateAsync({ action, assetId: asset.id });
            if (action === "reprocess") {
              showToast({ msg: "Re-queued for AI", type: "success", duration: "medium" });
            } else {
              deselectAssetIds([asset.id]);
            }
            advanceOrCloseOverlay(asset.id);
          } catch (error) {
            toastError(error);
            throw error;
          }
        }}
      />
    </div>
  );
}
